import { userSelectionDisplayToolSignature } from '@andsend/services-agent-shared';
import { makeAssistantToolUI, ThreadPrimitive } from '@assistant-ui/react';
import { Button } from '@shadcn/ui/components/ui/button';
import { ListFilter, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

type UserSelectionToolArgs = z.infer<typeof userSelectionDisplayToolSignature.inputSchema>;
type UserSelectionToolResult = z.infer<typeof userSelectionDisplayToolSignature.outputSchema>;

const SparklesIcon = () => {
    return <Sparkles className="h-4 w-4 shrink-0 text-text-brand-secondary" />;
};

/**
 * Component to display the selection options as buttons
 */
const UserSelectionUI = ({
    args,
    result,
    status,
}: {
    args: UserSelectionToolArgs;
    result: UserSelectionToolResult;
    status: 'running' | 'complete' | 'incomplete' | 'requires-action';
}) => {
    const [selectedOption, setSelectedOption] = useState<string | null>(null);

    if (args.options == undefined) return null;

    // No options to display
    if (!args.options || args.options.length === 0) {
        return (
            <div className="flex flex-row items-center gap-2">
                <SparklesIcon />
                <p>No selection options to display</p>
            </div>
        );
    }

    if (status === 'running') {
        return (
            <div className="flex flex-row items-center gap-2">
                <SparklesIcon />
                <p>Preparing selection options...</p>
            </div>
        );
    }

    // If the status is complete and we have a selected option or result
    if (status === 'complete') {
        // First check our local state
        if (selectedOption) {
            return (
                <div className="flex flex-row items-center gap-2">
                    <SparklesIcon />
                    <p>Selected: {selectedOption}</p>
                </div>
            );
        }
    }

    // This function takes a name string and an optional id
    const handleSelect = (name: string) => {
        // Create the selected option with the required properties
        const selectedOption = name;

        // Update local state
        setSelectedOption(selectedOption);
    };

    // Get the headline text from args if provided, otherwise use default
    const headlineText = args.headline || 'Suggested actions:';

    return (
        <div className="flex flex-col gap-2 p-2">
            <div className="flex flex-row items-center gap-2 mb-2">
                <SparklesIcon />
                <p>{headlineText}</p>
            </div>
            <div className="flex flex-col gap-2 w-full max-w-[500px]">
                {args.options.map((option, index) => {
                    // Skip if option or name is not defined
                    if (!option || !option.name) return null;

                    // Create a message to send when the option is selected
                    const message = `${option.name}`;

                    return (
                        <ThreadPrimitive.Suggestion key={`option-${index}`} prompt={message} method="replace" autoSend>
                            <Button
                                variant="outline"
                                className="w-full justify-start text-left truncate flex items-center gap-2 py-3"
                                onClick={() => handleSelect(option.name)}
                            >
                                <SparklesIcon />
                                <span>{option.name}</span>
                            </Button>
                        </ThreadPrimitive.Suggestion>
                    );
                })}
            </div>
        </div>
    );
};

/**
 * Tool UI component for the user selection tool
 */
export const UserSelectionToolUI = makeAssistantToolUI<UserSelectionToolArgs, UserSelectionToolResult>({
    toolName: userSelectionDisplayToolSignature.name,
    render: (props) => {
        const { args, result, status } = props;

        return UserSelectionUI({
            args,
            result,
            status: status.type,
        });
    },
});
